import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { Commitment, Invoice, Vendor } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Invoices() {
  const { persona } = useAuth();
  const { voteByCode } = useRefData();
  const vote = voteByCode(persona?.voteCode);
  const [status, setStatus] = useState('');
  const [showCapture, setShowCapture] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (status) p.set('status', status);
    return `/api/v1/expenditure/invoices?${p}`;
  }, [status]);
  const listQ = useApiQuery<{ items: Invoice[] }>(query);
  const vendorsQ = useApiQuery<{ items: Vendor[] }>('/api/v1/commitments/vendors?limit=100');
  const vendorName = useMemo(() => {
    const m = new Map((vendorsQ.data?.items ?? []).map((v) => [v.id, v.name]));
    return (id: string) => m.get(id) ?? id;
  }, [vendorsQ.data]);

  const canCapture = persona?.roles.includes('MDA_CERTIFYING_OFFICER') ?? false;

  const match = async (inv: Invoice) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/expenditure/invoices/${inv.id}/match`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Match failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Supplier invoices"
        subtitle="Capture incoming invoices, match them against commitments, then build payment vouchers"
        actions={
          canCapture ? (
            <button className="btn-primary" onClick={() => setShowCapture(true)}>
              + Capture invoice
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-4">
        <select className="field-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="CAPTURED">Captured</option>
          <option value="MATCHED">Matched</option>
          <option value="VOUCHERED">Vouchered</option>
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No invoices" hint="Capture the first supplier invoice against an approved commitment." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Invoice no.</th>
                <th className="table-head">Supplier</th>
                <th className="table-head">Date</th>
                <th className="table-head text-right">Amount</th>
                <th className="table-head">Document</th>
                <th className="table-head">Match</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{inv.number}</td>
                  <td className="table-cell">{vendorName(inv.vendorId)}</td>
                  <td className="table-cell text-slate-500">{formatDate(inv.date)}</td>
                  <td className="table-cell text-right tabular-nums">{scr(inv.amount)}</td>
                  <td className="table-cell text-xs text-slate-500">{inv.docRef}</td>
                  <td className="table-cell">
                    {inv.match ? (
                      <div className="text-xs">
                        <StatusBadge status={inv.match.status} />
                        <div className="mt-0.5 text-slate-400">{inv.match.type.replaceAll('_', '-')}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">not run</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={inv.status} />
                  </td>
                  <td className="table-cell text-right">
                    {canCapture && inv.status === 'CAPTURED' ? (
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => match(inv)}>
                        Run match
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCapture && vote ? (
        <CaptureModal
          voteId={vote.id}
          vendors={vendorsQ.data?.items ?? []}
          onClose={() => setShowCapture(false)}
          onDone={() => {
            setShowCapture(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CaptureModal({
  voteId,
  vendors,
  onClose,
  onDone,
}: {
  voteId: string;
  vendors: Vendor[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({ vendorId: '', commitmentId: '', number: '', date: '', amount: '', docRef: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const commitmentsQ = useApiQuery<{ items: Commitment[] }>(`/api/v1/commitments/?voteId=${voteId}&limit=100`);
  const eligible = (commitmentsQ.data?.items ?? []).filter(
    (c) =>
      (!form.vendorId || c.vendorId === form.vendorId) &&
      ['APPROVED', 'PO_ISSUED', 'PARTIALLY_LIQUIDATED'].includes(c.status),
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/expenditure/invoices', {
        vendorId: form.vendorId,
        commitmentId: form.commitmentId || undefined,
        number: form.number,
        date: form.date,
        amount: Number.parseFloat(form.amount),
        docRef: form.docRef,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to capture invoice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Capture supplier invoice" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Supplier">
            <select
              className="field-input"
              value={form.vendorId}
              onChange={(e) => setForm({ ...form, vendorId: e.target.value, commitmentId: '' })}
            >
              <option value="">Select supplier…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Against commitment">
            <select
              className="field-input"
              value={form.commitmentId}
              onChange={(e) => setForm({ ...form, commitmentId: e.target.value })}
            >
              <option value="">Select commitment…</option>
              {eligible.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.refNumber} — {scr(c.amount)} — {c.purpose.slice(0, 60)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Invoice number">
            <input className="field-input" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
          </Field>
          <Field label="Invoice date">
            <input
              className="field-input"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Field>
          <Field label="Amount (SCR)">
            <input
              className="field-input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Scanned document reference (DocVault)">
          <input
            className="field-input"
            value={form.docRef}
            onChange={(e) => setForm({ ...form, docRef: e.target.value })}
            placeholder="e.g. DOCV-MTC-2026-4471"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !form.vendorId || !form.number || !form.date || !form.amount || !form.docRef}
          >
            {busy ? 'Capturing…' : 'Capture invoice'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
